import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {OntologyListEntity, RelatableSelectableItem, ScenarioForDisplayability} from '../../FilterBannerModels';
import {isItemAbleToShow} from '../../UpdatedFilterBannerHelpers';

@Component({
  selector: 'app-search-department',
  templateUrl: './search-department.component.html',
  styleUrls: ['./search-department.component.scss']
})
export class SearchDepartmentComponent implements OnChanges, OnInit {

  @Input() departments: RelatableSelectableItem<OntologyListEntity>[] = [];
  @Input() whenToggled: (open: boolean) => void;
  @Input() showSpecialties = false;
  @Output() afterDepartmentsUpdated = new EventEmitter<boolean>();
  selectedDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  unselectedDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  displayedSelectedDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  displayedUnselectedDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  hiddenSelectedDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  hiddenUnselectedDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  showDepartments = false;
  readonly DEFAULT_DEPARTMENTS = 'All Departments';
  toggleLabel = this.DEFAULT_DEPARTMENTS;
  additionalLabel = '';
  toggleInfo: BehaviorSubject<boolean>;
  searchText = '';

  constructor() {
    this.toggleInfo = new BehaviorSubject<boolean>(false);
    this.getToggleInfo().subscribe((showDepartments: boolean) => {
      this.showDepartments = showDepartments;
      if (this.whenToggled) {
        this.whenToggled(showDepartments);
      }
    });
  }

  ngOnInit(): void {
    this.filterDepartmentLists();
    this.setToggleLabel();
  }

  ngOnChanges() {
    if (this.showSpecialties) {
      this.toggleInfo.next(false);
    }
  }

  private filterDepartmentLists(): void {
    this.selectedDepartments = this.departments.filter(d => d.item.selected);
    this.unselectedDepartments = this.departments.filter(d => !d.item.selected);
  }

  private filterDisplayedDepartments(): void {
    const allSelectedDisplayedDepartments = this.displayedSelectedDepartments.concat(this.hiddenSelectedDepartments);
    this.displayedSelectedDepartments = allSelectedDisplayedDepartments.filter(d =>
      isItemAbleToShow(d, ScenarioForDisplayability.Standard));
    this.hiddenSelectedDepartments = allSelectedDisplayedDepartments.filter(d => !isItemAbleToShow(d,
      ScenarioForDisplayability.Standard));
    const allUnselectedDisplayedDepartments = this.displayedUnselectedDepartments.concat(this.hiddenUnselectedDepartments);
    this.displayedUnselectedDepartments = allUnselectedDisplayedDepartments.filter(d =>
      isItemAbleToShow(d, ScenarioForDisplayability.Standard));
    this.hiddenUnselectedDepartments = allUnselectedDisplayedDepartments.filter(d => !isItemAbleToShow(d,
      ScenarioForDisplayability.Standard));
  }

  getToggleInfo(): Observable<boolean> {
    return this.toggleInfo.asObservable();
  }

  toggleDepartments(): void {
    if (!this.toggleInfo.value) {
      this.displayedSelectedDepartments = this.selectedDepartments.slice();
      this.displayedUnselectedDepartments = this.unselectedDepartments.slice();
    }
    this.toggleInfo.next(!this.toggleInfo.value);
  }

  onSearchTextChanged(): void {
    this.departments.forEach(department => {
      department.item.matchesSearchText = this.searchText.length < 3 ||
        !!department.item.item.departmentNodeName?.toLowerCase().includes(this.searchText.toLowerCase());
    });
    this.filterDisplayedDepartments();
  }

  selectAllDepartments(event: MouseEvent): void {
    event.preventDefault();
    if (this.displayedSelectedDepartments.length && !this.displayedUnselectedDepartments.length) {
      this.displayedSelectedDepartments.forEach(department => {
        department.item.selected = false;
      });
      this.displayedUnselectedDepartments = this.displayedUnselectedDepartments.concat(this.displayedSelectedDepartments);
      this.displayedSelectedDepartments = [];
    } else {
      this.displayedUnselectedDepartments.forEach(department => {
        department.item.selected = true;
      });
      this.displayedSelectedDepartments = this.displayedSelectedDepartments.concat(this.displayedUnselectedDepartments);
      this.displayedUnselectedDepartments = [];
    }
  }

  whenDepartmentClicked(department: RelatableSelectableItem<OntologyListEntity>, event: MouseEvent): void {
    event.preventDefault();
    department.item.selected = !department.item.selected;
    if (department.item.selected) {
      this.displayedUnselectedDepartments = this.displayedUnselectedDepartments.filter(d => d !== department);
      this.displayedSelectedDepartments.push(department);
    } else {
      this.displayedSelectedDepartments = this.displayedSelectedDepartments.filter(d => d !== department);
      this.displayedUnselectedDepartments.push(department);
    }
  }

  private setToggleLabel(): void {
    if (this.selectedDepartments.length === 0 || this.unselectedDepartments.length === 0) {
      this.toggleLabel = this.DEFAULT_DEPARTMENTS;
    } else {
      this.toggleLabel = this.selectedDepartments[0].item.item.departmentNodeName || this.toggleLabel;
      this.additionalLabel = '{+ ' + (this.selectedDepartments.length - 1) + ' More}';
    }
  }

  cancel(): void {
    this.toggleInfo.next(false);
  }

  apply(): void {
    this.toggleInfo.next(false);
    this.selectedDepartments = this.displayedSelectedDepartments.slice();
    this.unselectedDepartments = this.displayedUnselectedDepartments.slice();
    this.filterDepartmentLists();
    this.afterDepartmentsUpdated.emit(!!this.selectedDepartments.length);
    this.setToggleLabel();
  }
}
